import React, { createContext, useContext, useState, useCallback } from 'react';

interface LoadingContextType {
  isLoading: boolean;
  loadingMessage: string;
  setLoading: (loading: boolean, message?: string) => void;
  setTabLoading: (loading: boolean, tabKey?: string) => void;
  isTabLoading: (tabKey: string) => boolean;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [tabLoadingStates, setTabLoadingStates] = useState<Record<string, boolean>>({});

  const setLoading = useCallback((loading: boolean, message: string = '') => {
    setIsLoading(loading);
    setLoadingMessage(message);
  }, []);

  const setTabLoading = useCallback((loading: boolean, tabKey: string = 'default') => {
    setTabLoadingStates(prev => ({
      ...prev,
      [tabKey]: loading,
    }));
  }, []);

  const isTabLoading = useCallback(
    (tabKey: string) => {
      return tabLoadingStates[tabKey] || false;
    },
    [tabLoadingStates]
  );

  return (
    <LoadingContext.Provider
      value={{
        isLoading,
        loadingMessage,
        setLoading,
        setTabLoading,
        isTabLoading,
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};
